import { ColDef } from 'ag-grid-community';
import { EMPTY } from 'rxjs';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';

import { ApmTranslatePipe, AuthFacade, PermissionUtils } from '@mitsubishi/core';
import { PriceListService } from '@mitsubishi/pricing-list/data-access';
import { ButtonComponent } from '@mitsubishi/shared/ui';
import {
  ApmGridColumnType,
  ServerSideDataSource,
  ServerSideTableComponent,
  TableResourceHeaderComponent,
  TableResourceHeaderComponentParams,
} from '@mitsubishi/ui/table';

import { PriceListDetailsCellComponent } from '../price-list-details-cell/price-list-details-cell.component';
import { PriceListSubfamilyGroupCellComponent } from '../price-list-subfamily-group-cell/price-list-subfamily-group-cell.component';
import { PriceListChangelogDialogData } from './price-list-changelog-dialog.model';

@Component({
  selector: 'apm-price-list-changelog-dialog',
  imports: [ApmTranslatePipe, ServerSideTableComponent, ButtonComponent, MatTabsModule, CommonModule],
  templateUrl: './price-list-changelog-dialog.component.html',
  styleUrl: './price-list-changelog-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceListChangelogDialogComponent implements OnInit {
  readonly #pricingListService = inject(PriceListService);
  readonly #authFacade = inject(AuthFacade);

  readonly #dialogData = inject<PriceListChangelogDialogData>(MAT_DIALOG_DATA);
  readonly #dialogRef = inject(MatDialogRef<PriceListChangelogDialogComponent>);

  readonly cacheBlockSize = 20;

  readonly selectedIndex = signal(0);
  readonly priceListColumnDefs = signal<ColDef[]>([]);
  readonly markupColumnDefs = signal<ColDef[]>([]);
  readonly extendedWarrantiesColumnDefs = signal<ColDef[]>([]);

  readonly canViewMarkup = PermissionUtils.hasViewMarkupPermission();

  // Data sources
  readonly priceListDataSource: ServerSideDataSource = ({ page, pageSize }) =>
    this.#pricingListService.loadChangeLogPricelists(
      this.#dialogData.rows,
      this.#dialogData.viewFullHistory,
      page,
      pageSize
    );

  readonly markupDataSource: ServerSideDataSource = ({ page, pageSize }) => {
    const orgId = this.#dialogData.organization?.()?.id;
    if (orgId === undefined) return EMPTY;
    return this.#pricingListService.loadChangelogMarkup(
      this.#dialogData.rows,
      orgId,
      this.#dialogData.viewFullHistory,
      page,
      pageSize
    );
  };

  readonly extendedWarrantiesDataSource: ServerSideDataSource = ({ page, pageSize }) =>
    this.#pricingListService.loadChangeLogExtendedWarranties(
      this.#dialogData.rows,
      this.#dialogData.viewFullHistory,
      page,
      pageSize
    );

  constructor() {
    this.#dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    const orgId = this.#dialogData.organization?.()?.id;
    if (orgId === undefined) return;

    this.#initializeColumnDefs(orgId);
  }

  closeDialog(): void {
    this.#dialogRef.close();
  }

  #initializeColumnDefs(orgId: number): void {
    const notesField = this.#authFacade.userProfile.languageCode === 'it-IT' ? 'changelogIt' : 'changelogEn';

    this.priceListColumnDefs.set([
      this.#createSubFamilyCell(orgId),
      this.#createPriceListCell('445582'),
      this.#createNotesCell(notesField),
    ]);

    this.markupColumnDefs.set([
      this.#createSubFamilyCell(orgId),
      this.#createPriceListCell('451497'),
      this.#createNotesCell(),
    ]);

    this.extendedWarrantiesColumnDefs.set([
      this.#createSubFamilyCell(orgId),
      this.#createPriceListCell('451503'),
      this.#createNotesCell(),
    ]);
  }

  #createSubFamilyCell(orgId: number): ColDef {
    return {
      autoHeight: true,
      sortable: false,
      suppressMenu: true,
      cellRenderer: PriceListSubfamilyGroupCellComponent,
      type: ApmGridColumnType.CENTERED_CONTENT,
      cellRendererParams: {
        suppressCount: true,
        hideActions: true,
        showAlerts: false,
        explicitLevel: 2,
        hideSegmentBorder: true,
        organization: orgId,
      },
    };
  }

  #createNotesCell(field = 'changelog'): ColDef {
    return {
      headerComponent: TableResourceHeaderComponent,
      headerComponentParams: {
        resourceId: '445012',
      } as TableResourceHeaderComponentParams,
      field,
      wrapText: true,
      autoHeight: true,
      type: [ApmGridColumnType.PRE_WRAP, ApmGridColumnType.ALIGN_LEFT],
      sortable: false,
      suppressMenu: true,
      minWidth: 300,
      cellStyle: { 'line-height': 1.5 },
    };
  }

  #createPriceListCell(resourceId: string): ColDef {
    return {
      headerComponent: TableResourceHeaderComponent,
      headerComponentParams: {
        resourceId,
      } as TableResourceHeaderComponentParams,
      wrapText: true,
      autoHeight: true,
      sortable: false,
      suppressMenu: true,
      type: [ApmGridColumnType.CENTERED_CONTENT, ApmGridColumnType.PRE_WRAP],
      cellStyle: { 'line-height': 1.5 },
      cellRenderer: PriceListDetailsCellComponent,
      cellRendererParams: {
        dataAtRootLevel: true,
      },
    };
  }
}
